import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
// import "./ModalCadastroParcela.scss";
import Swal from "sweetalert2";
import { Label } from "../Label/styles";
import InputComponent from "../../components/Input/input";
import Button from "../../components/Button/button";
import SelectComponent from "../Select/select";
import FichaFinanceiraService from "../../services/FichaFinanceiraService";
import {
  BlueTitle,
  ModalBody,
  ModalBodyContainer,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  TextError,
} from "../../shared/styles/global.styles";
import { CreateFinancialRecordDto } from "../../shared/dtos/FichaFinanceira.dto";
import lotacaoService from "../../services/LotacaoService";
import { Lotacao } from "../../shared/interfaces/DadosFinanceirosInterface";
import { StatusImpressaoEnum } from "../../shared/enum/fichaFinanceira.enum";
import { IoCloseSharp } from "react-icons/io5";
import { progressBarSwalAlert } from "../ProgressBarSwalAlert/ProgressBarSwalAlert";
import { Colors } from "../../shared/colors/colors";
import requestInProgressService from "../../services/RequestInProgressService";

// Define os tipos das props para o componente
interface CadastroParcelaProps {
  onSubmit: (
    parcelas: CreateFinancialRecordDto[],
    idContribuinte: number
  ) => void;
  onClose: () => void;
  idContribuinte: number | number[];
  dadosFinanceiros?: {
    imprimir: boolean;
    dataExclusao?: string | null;
    lotacao?: Lotacao;
  };
  onParcelaCadastrada: () => void;
  show: boolean;
}

const CadastroParcela: React.FC<CadastroParcelaProps> = ({
  onSubmit,
  onClose,
  idContribuinte,
  dadosFinanceiros,
  onParcelaCadastrada,
  show,
}) => {
  const hoje = new Date();
  const [parcela, setParcela] = useState<CreateFinancialRecordDto>({
    statusParcela: "Ativa",
    sequencialParcela: "1",
    nossoNumero: "0",
    tipoCobranca: "Mensalidade",
    valorParcela: "",
    dataVencimentoInicio: hoje.toISOString().split("T")[0],
    dataVencimentoFim: hoje.toISOString().split("T")[0],
    valorAjuste: "0",
    valorPago: "0",
    valorTarifa: "0",
    dataPagamento: "",
    statusImpressao: dadosFinanceiros?.imprimir
      ? StatusImpressaoEnum.A_IMPRIMIR
      : StatusImpressaoEnum.NAO_IMPRIMIR,
    statusRemessa: "Enviar",
    statusRemessaBaixa: "Enviar",
    tipoExclusao: null,
    dataGeracao: hoje.toISOString().split("T")[0],
    usuarioGeracao: "",
    codigoBarras: "",
    qrCodePix: "",
    numeroRemessa: 0,
    numeroRetorno: 0,
    dataCaixa: hoje.toISOString().split("T")[0],
    contribuinte: Array.isArray(idContribuinte)
      ? { id: idContribuinte[0], nome: "Nome do Contribuinte" }
      : { id: idContribuinte, nome: "Nome do Contribuinte" },
    idLotacao:
      dadosFinanceiros?.lotacao && dadosFinanceiros?.lotacao !== null
        ? dadosFinanceiros.lotacao.id
        : undefined,
  });
  const [isRequest, setIsRequest] = useState(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [lotacoes, setLotacoes] = useState<Lotacao[]>([]);
  const [selectLotacao, setSelectLotacao] = useState<Lotacao>(
    dadosFinanceiros?.lotacao && dadosFinanceiros?.lotacao !== null
      ? dadosFinanceiros.lotacao
      : ({} as Lotacao)
  );

  // Função para formatar valores monetários
  const formatCurrency = (value: string) => {
    value = value.replace(/\D/g, ""); // Remove caracteres não numéricos
    value = (parseFloat(value) / 100).toFixed(2).replace(".", ","); // Formata como valor monetário
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, "."); // Adiciona separadores de milhar
  };

  const handleChangeValues = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Aplica formatação monetária para valores específicos
    let formattedValue = value;
    if (["valorParcela"].includes(name)) {
      formattedValue = formatCurrency(value);
    }

    setParcela({ ...parcela, [name]: formattedValue });
  };

  // Função para atualizar os valores dos campos
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Aplica formatação monetária para valores específicos
    let formattedValue = value;
    if (
      ["valorParcela", "valorAjuste", "valorPago", "valorTarifa"].includes(name)
    ) {
      formattedValue = formatCurrency(value || "0");
    }

    setParcela({ ...parcela, [name]: formattedValue || "" });
  };

  const handleDateChange = (date: string, field: string) => {
    setParcela({ ...parcela, [field]: date });
  };

  const clearError = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    switch (name) {
      case "valorParcela":
        setInvalidFields((prev) => prev.filter((field) => field !== name));
        break;
    }
  };

  // Verifica se os dados financeiros estão ativos
  const verificarDadosFinanceirosAtivos = () => {
    if (!dadosFinanceiros) {
      Swal.fire({
        icon: "error",
        title: "Dados Financeiros Inválidos",
        text: "Não é possível cadastrar parcelas pois os dados financeiros do contribuinte estão inativos. Para prosseguir, é necessário reativar o dado financeiro.",
        confirmButtonText: "Entendi",
        allowOutsideClick: false,
      });
      setIsRequest(false);
      return false;
    }

    if (dadosFinanceiros.dataExclusao) {
      Swal.fire({
        icon: "error",
        title: "Dados Financeiros Inválidos",
        text: "Não é possível cadastrar parcelas pois os dados financeiros do contribuinte estão inativos. Para prosseguir, é necessário reativar o dado financeiro.",
        confirmButtonText: "Entendi",
        allowOutsideClick: false,
      });
      setIsRequest(false);
      return false;
    }
    setIsRequest(false);
    return true;
  };

  const clearInvalidFields = () => setInvalidFields([]);

  // Validação dos dados do formulário
  const validarFormulario = () => {
    clearInvalidFields();
    const errors = [];
    const dateNow = new Date().toISOString().split("T")[0] + "T00:00:00.000Z";
    if (!parcela.dataVencimentoInicio || !parcela.dataVencimentoFim) {
      errors.push("A data de vencimento é obrigatória");
      setInvalidFields((prev) => [...prev, "dataInicio", "dataFim"]);
    } else {
      if (
        new Date(`${parcela.dataVencimentoInicio}T00:00:00`) < new Date(dateNow)
      ) {
        errors.push("A data de vencimento não pode ser menor que a data atual");
        setInvalidFields((prev) => [...prev, "dataInicio"]);
      } else {
        if (
          new Date(`${parcela.dataVencimentoFim}T00:00:00`) < new Date(dateNow)
        ) {
          errors.push(
            "A data de vencimento fim não pode ser menor que a data atual"
          );
          setInvalidFields((prev) => [...prev, "dataFim"]);
        } else {
          if (
            new Date(`${parcela.dataVencimentoFim}T00:00:00`) <
            new Date(`${parcela.dataVencimentoInicio}T00:00:00`) &&
            parcela.tipoCobranca === "Mensalidade" 
          ) {
            errors.push(
              "A data de vencimento fim não pode ser menor que a data de vencimento início"
            );
            setInvalidFields((prev) => [...prev, "dataFim"]);
          } else {
            setInvalidFields((prev) =>
              prev.filter(
                (field) => field !== "dataInicio" && field !== "dataFim"
              )
            );
          }
        }
      }
    }

    if (parcela.tipoCobranca === "Esporádica") {
      if (
        !parcela.valorParcela ||
        parseFloat(parcela.valorParcela.replace(/\./g, "").replace(",", ".")) <=
          0
      ) {
        errors.push("O valor da parcela deve ser maior que zero");
        setInvalidFields((prev) => [...prev, "valorParcela"]);
      } else {
        setInvalidFields((prev) =>
          prev.filter((field) => field !== "valorParcela")
        );
      }
    }

    if (errors.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Dados Inválidos",
        html: errors.map((error) => `• ${error}`).join("<br>"),
        confirmButtonText: "Corrigir",
        allowOutsideClick: false,
      });
      setIsRequest(false);
      return false;
    }
    setIsRequest(false);
    return true;
  };

  // const calcularValorTotal = async () => {
  //   try {
  //     // Busca dados financeiros ativos
  //     const dadosFinanceiros =
  //       await dadosFinanceirosService.getDadosFinanceirosByContribuinte(
  //         idContribuinte as number
  //       );
  //     const dadoAtivo = dadosFinanceiros.find(
  //       (dado: GetDadosFinanceirosDto) => !dado.dataExclusao
  //     );

  //     if (!dadoAtivo) {
  //       throw new Error(
  //         "Não há dados financeiros ativos para este contribuinte"
  //       );
  //     }


  //     // Valor base do titular (ex: 15,00)
  //     const valorTitular = Number(dadoAtivo.valorTitular) || 0;
  //     let valorTotal = valorTitular;

  //     try {
  //       // Busca dependentes ativos (incluindo reativados)
  //       const dependentesAtivos =
  //         await dadosFinanceirosService.getDependentesAtivos(
  //           idContribuinte as number
  //         );


  //       // Adiciona valor para cada dependente ativo ou reativado
  //       if (dependentesAtivos && dependentesAtivos.length > 0) {
  //         // Valor por dependente (ex: 5,00)
  //         const valorPorDependente = Number(dadoAtivo.valorDependente) || 0;
  //         // Multiplica pelo número de dependentes (ex: 5,00 × 2 = 10,00)
  //         const valorTotalDependentes =
  //           valorPorDependente * dependentesAtivos.length;
  //         // Soma ao valor do titular (ex: 15,00 + 10,00 = 25,00)
  //         valorTotal = valorTitular + valorTotalDependentes;

  //           valorTitularOriginal: dadoAtivo.valorTitular,
  //           valorTitularConvertido: valorTitular,
  //           valorDependenteOriginal: dadoAtivo.valorDependente,
  //           valorDependenteConvertido: valorPorDependente,
  //           numeroDependentes: dependentesAtivos.length,
  //           valorTotalDependentes,
  //           valorTotalGeral: valorTotal,
  //           dependentesAtivos: dependentesAtivos.map((d) => ({
  //             id: d.id,
  //             nome: d.nome,
  //             status: d.status,
  //           })),
  //         });
  //       } else {
  //           valorTitularOriginal: dadoAtivo.valorTitular,
  //           valorTitularConvertido: valorTitular,
  //           valorTotal: valorTitular,
  //         });

  //         Swal.fire({
  //           title: "Aviso",
  //           text: "Não há dependentes em situação de Inclusão ou Reativação. O valor será calculado apenas com o titular.",
  //           icon: "info",
  //         });
  //       }
  //     } catch (dependentesError) {
  //       console.error("Erro ao buscar dependentes:", dependentesError);
  //       Swal.fire({
  //         title: "Erro",
  //         text: "Não foi possível carregar os dependentes. O valor será calculado apenas com o titular.",
  //         icon: "warning",
  //       });
  //     }
  //     setIsRequest(false);
  //     return valorTotal;
  //   } catch (error) {
  //     console.error("Erro ao calcular valor total:", error);
  //     throw error;
  //   }
  // };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsRequest(true);

    // Verifica os dados financeiros antes de qualquer processamento
    if (!verificarDadosFinanceirosAtivos()) {
      setIsRequest(false);
      return;
    }

    // Valida os dados do formulário
    if (!validarFormulario()) {
      setIsRequest(false);
      return;
    }

    try {
      if (parcela.tipoCobranca === "Mensalidade") {
        // Para o tipo "Mensalidade", criamos as parcelas sem o valor
        const parcelas: CreateFinancialRecordDto[] = [];
        const dataInicio = new Date(parcela.dataVencimentoInicio);
        const dataFim = new Date(parcela.dataVencimentoFim);
        dataFim.setMonth(dataFim.getMonth() + 12); // ajusta a data fim para 1 ano após a data início

        // Cria uma nova parcela sem o valor da parcela
        const novaParcela: CreateFinancialRecordDto = {
          ...parcela,
          sequencialParcela: "1",
          dataVencimentoInicio: dataInicio.toISOString().split("T")[0],
          dataVencimentoFim: dataFim.toISOString().split("T")[0],
          valorParcela: "", // Não envia o valor da parcela para o tipo "Mensalidade"
          statusImpressao: dadosFinanceiros?.imprimir
            ? StatusImpressaoEnum.A_IMPRIMIR
            : StatusImpressaoEnum.NAO_IMPRIMIR,
          dataCaixa: hoje.toISOString().split("T")[0],
          idLotacao: selectLotacao.id,
        };

        parcelas.push(novaParcela);

        // Agora, para o tipo "Mensalidade", usamos o método cadastrarVariasParcelas
        const contribuintes = Array.isArray(idContribuinte)
          ? idContribuinte.map((id) => ({ id, nome: "" })) // Substitua '' por nomes reais dos contribuintes
          : [{ id: idContribuinte, nome: "" }];

        const params = {
          contribuintes: contribuintes,
          parcelas: [
            {
              dataVencimentoInicio: parcela.dataVencimentoInicio,
              dataVencimentoFim: parcela.dataVencimentoFim,
              idLotacao: selectLotacao.id,
            },
          ] as [{}],
        };

        // Chama o método de cadastro para várias parcelas
        try {
          Swal.fire({
            icon: "info",
            title: "Aguarde",
            text: "Cadastrando parcelas, por favor aguarde",
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });
          await FichaFinanceiraService.cadastrarVariasParcelas(params).then(
            async (responseCreateFicha) => {
              if (responseCreateFicha.success) {
                Swal.fire({
                  title: "Processando solicitação",
                  html: progressBarSwalAlert({
                    currentProgress: 0,
                    totalProgress: 0,
                    color: Colors.primary,
                    showProgressText: true,
                  }),
                  showConfirmButton: false,
                  allowOutsideClick: false,
                  didOpen: () => {
                    Swal.showLoading();
                  },
                });

                let stopLoopCreateFicha = false;
                let getRequestInProgressCreateFicha =
                  await requestInProgressService.getRequestInProgressById(
                    responseCreateFicha.data.id
                  );
                await new Promise((resolve) => setTimeout(resolve, 2000));

                while (!stopLoopCreateFicha) {
                  getRequestInProgressCreateFicha =
                    await requestInProgressService.getRequestInProgressById(
                      responseCreateFicha.data.id
                    );

                  if (getRequestInProgressCreateFicha.finished) {
                    stopLoopCreateFicha = true;
                  } else {
                    Swal.update({
                      html: progressBarSwalAlert({
                        currentProgress:
                          getRequestInProgressCreateFicha.currentProgress,
                        totalProgress:
                          getRequestInProgressCreateFicha.totalProgress,
                        color: Colors.primary,
                        showProgressText: true,
                        progressText: "Cadastrando parcelas aguarde...",
                      }),
                    });
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                  }
                }
                await requestInProgressService.deleteRequestInProgress(
                  responseCreateFicha.data.id
                );
                if (!getRequestInProgressCreateFicha.error) {
                  const response = JSON.parse(
                    getRequestInProgressCreateFicha.responseData
                  );
                  Swal.fire({
                    icon: "success",
                    title: "Processo Finalizado",
                    html: `O processo foi concluído com sucesso. <strong>${response.totalParcelasCriadas}</strong> parcelas foram geradas.`,
                    confirmButtonText: "OK",
                    allowOutsideClick: false,
                  }).then(() => {
                    onSubmit(
                      parcelas,
                      Array.isArray(idContribuinte)
                        ? idContribuinte[0]
                        : idContribuinte
                    );
                    onClose();
                    onParcelaCadastrada();
                  });
                } else {
                  Swal.fire({
                    title: "Erro",
                    text: getRequestInProgressCreateFicha.message,
                    icon: "error",
                    confirmButtonText: "OK",
                    allowOutsideClick: false,
                  });
                }
              } else {
                Swal.fire({
                  title: "Erro",
                  text: "Ocorreu um erro ao solicitar a criação das parcelas. Por favor, tente novamente.",
                  icon: "error",
                  confirmButtonText: "OK",
                  allowOutsideClick: false,
                });
              }
            }
          );
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Erro ao cadastrar parcelas",
            text: "Ocorreu um erro ao cadastrar as parcelas, por favor tente novamente",
            allowOutsideClick: false,
          });
          console.error("Erro ao cadastrar parcelas:", error);
        }
      } else if (parcela.tipoCobranca === "Esporádica") {
        // Validação dos campos obrigatórios para cobrança esporádica
        if (!parcela.valorParcela || !parcela.dataVencimentoInicio) {
          Swal.fire({
            icon: "error",
            title: "Campos obrigatórios",
            text: "Por favor, preencha todos os campos obrigatórios.",
            allowOutsideClick: false,
          });
          setIsRequest(false);
          return;
        }

        // Validação do valor da parcela apenas para "Esporádica"
        const valorParcela = parseFloat(
          parcela.valorParcela.toString().replace(/\./g, "").replace(",", ".")
        );
        if (isNaN(valorParcela) || valorParcela <= 0) {
          Swal.fire({
            icon: "error",
            title: "Valor inválido",
            text: "O valor da parcela deve ser maior que zero.",
            allowOutsideClick: false,
          });
          setIsRequest(false);
          return;
        }

        const parcelas: CreateFinancialRecordDto[] = [];
        for (let i = 0; i < 1; i++) {
          const novaParcela: CreateFinancialRecordDto = {
            ...parcela,
            sequencialParcela: (i + 1).toString(),
            dataVencimentoInicio: parcela.dataVencimentoInicio,
            dataVencimentoFim: parcela.dataVencimentoInicio,
            valorParcela: parseFloat(
              parcela.valorParcela
                .toString()
                .replace(/\./g, "")
                .replace(",", ".")
            ).toString(),
            statusImpressao: dadosFinanceiros?.imprimir
              ? StatusImpressaoEnum.A_IMPRIMIR
              : StatusImpressaoEnum.NAO_IMPRIMIR,
            dataCaixa: hoje.toISOString().split("T")[0],
            idLotacao: selectLotacao.id,
          };

          parcelas.push(novaParcela);
        }
        try {
          Swal.fire({
            icon: "info",
            title: "Aguarde",
            text: "Cadastrando parcelas, por favor aguarde",
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          // Para o tipo "Esporádica", chama o método cadastrarParcela
          if (Array.isArray(idContribuinte)) {
            // Se houver múltiplos contribuintes, faz o cadastro de cada um
            await Promise.all(
              idContribuinte.map((id) =>
                FichaFinanceiraService.cadastrarParcela(parcelas, id)
              )
            );
          } else {
            // Caso contrário, faz o cadastro de um único contribuinte
            await FichaFinanceiraService.cadastrarParcela(
              parcelas,
              idContribuinte
            );
          }
          onSubmit(
            parcelas,
            Array.isArray(idContribuinte) ? idContribuinte[0] : idContribuinte
          );
          onClose();
        } catch (error) {
          console.error("Erro ao cadastrar parcelas:", error);
          Swal.fire({
            icon: "error",
            title: "Erro",
            text: "Erro ao cadastrar as parcelas. Por favor, tente novamente.",
            allowOutsideClick: false,
          });
        }
      }
    } catch (error) {
      console.error("Erro ao processar o formulário:", error);
      Swal.fire({
        icon: "error",
        title: "Erro no Cadastro",
        text: "Ocorreu um erro ao tentar cadastrar as parcelas. Por favor, tente novamente.",
        confirmButtonText: "Ok",
        allowOutsideClick: false,
      });
    }
    setIsRequest(false);
  };

  const fetchLotacoes = async () => {
    try {
      const response = await lotacaoService.getAllLotacoes();
      setLotacoes(response);
    } catch (error) {
      Swal.fire({
        title: "Erro",
        text: "Erro ao buscar lotações. Por favor, tente novamente.",
        icon: "error",
        confirmButtonText: "Fechar",
        allowOutsideClick: false,
      });
    }
  };

  useEffect(() => {
    fetchLotacoes();
  }, []);

  useEffect(() => {
    setParcela({
      ...parcela,
      dataVencimentoInicio: hoje.toISOString().split("T")[0],
      dataVencimentoFim: hoje.toISOString().split("T")[0],
    });
  }, [parcela.tipoCobranca]);

  return (
    <ModalContainer show={show}>
      <ModalContent width={35}>
        <Button
          type="button"
          schemaButton="x-close"
          action={() => onClose()}
          icon={<IoCloseSharp size={30} />}
          position="absolute"
          top={10}
          right={10}
        />
        <ModalHeader>
          <BlueTitle>Cadastrar Parcelas</BlueTitle>
        </ModalHeader>
        <form onSubmit={handleSubmit} className="cadastro-parcela-form">
          <ModalBody>
            <div>
              <Label htmlFor="tipoCobranca">Tipo de Cobrança</Label>
              <SelectComponent
                color="black"
                placeholder="Selecione"
                isPlaceHolder={false}
                id="tipoCobranca"
                width={Number("100%")}
                name="tipoCobranca"
                value={parcela.tipoCobranca}
                onChange={handleChange}
                disabled={isRequest}
                options={[
                  { value: "Mensalidade", label: "Mensalidade" },
                  { value: "Esporádica", label: "Esporádica" },
                ]}
              />
            </div>
            <div>
              <Label htmlFor="lotacao">Lotação</Label>
              <SelectComponent
                color="black"
                placeholder="Sem lotação"
                isPlaceHolder={false}
                id="lotacao"
                width={Number("100%")}
                name="lotacao"
                disabled={isRequest}
                value={selectLotacao?.id ? selectLotacao?.id?.toString() : ""}
                onChange={(e) => {
                  const selectedLotacao = lotacoes.find(
                    (lotacao) => lotacao.id === parseInt(e.target.value)
                  );
                  setSelectLotacao(selectedLotacao || ({} as Lotacao));
                  clearError(e);
                }}
                options={lotacoes.map((lotacao) => ({
                  value: lotacao.id!.toString(),
                  label: lotacao.descricao,
                }))}
                errorMessage={
                  invalidFields.includes("lotacao")
                    ? "A seleção da lotação é obrigatória"
                    : ""
                }
              />
            </div>
            {parcela.tipoCobranca === "Mensalidade" && (
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="dataVencimento">Data Início</Label>
                  <InputComponent
                    type="date"
                    name="dataVencimentoInicio"
                    id="dataVencimentoInicio"
                    value={parcela.dataVencimentoInicio}
                    onChange={(e) =>
                      handleDateChange(e.target.value, "dataVencimentoInicio")
                    }
                    placeholder="00/00/0000"
                    color="#333"
                    width={Number("100%")}
                    border="1px solid #ccc"
                    disabled={isRequest}
                    errorMessage={
                      invalidFields.includes("dataInicio")
                        ? "Data de início inválida"
                        : ""
                    }
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="dataVencimento">Data Fim</Label>
                  <InputComponent
                    type="date"
                    name="dataVencimentoFim"
                    id="dataVencimentoFim"
                    value={parcela.dataVencimentoFim}
                    onChange={(e) =>
                      handleDateChange(e.target.value, "dataVencimentoFim")
                    }
                    placeholder="00/00/0000"
                    color="#333"
                    width={Number("100%")}
                    border="1px solid #ccc"
                    disabled={isRequest}
                    errorMessage={
                      invalidFields.includes("dataFim")
                        ? "Data de fim inválida"
                        : ""
                    }
                  />
                </div>
              </div>
            )}
            {parcela.tipoCobranca === "Esporádica" && (
              <>
                <div>
                  <Label htmlFor="dataVencimento">Data Vencimento</Label>
                  <InputComponent
                    type="date"
                    name="dataVencimentoInicio"
                    id="dataVencimentoInicio"
                    value={parcela.dataVencimentoInicio}
                    onChange={(e) => {
                      handleDateChange(e.target.value, "dataVencimentoInicio");
                    }}
                    placeholder="00/00/0000"
                    color="#333"
                    width={Number("100%")}
                    border="1px solid #ccc"
                    disabled={isRequest}
                    errorMessage={
                      invalidFields.includes("dataInicio")
                        ? "Data de início inválida"
                        : ""
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="valorParcela">
                    Valor da Parcela <TextError>*</TextError>
                  </Label>
                  <InputComponent
                    type="text"
                    name="valorParcela"
                    id="valorParcela"
                    value={
                      parcela.valorParcela ? `R$ ${parcela.valorParcela}` : ""
                    }
                    onChange={(e) => {
                      handleChangeValues(e);
                      clearError(e);
                    }}
                    placeholder="R$ 0,00"
                    disabled={isRequest}
                    color="black"
                    width={Number("100%")}
                    errorMessage={
                      invalidFields.includes("valorParcela")
                        ? "Preencha o campo valor da parcela"
                        : ""
                    }
                  />
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {/* <button type="submit" className="btn-submit">Cadastrar Parcelas</button> */}
            <Button schemaButton="full-primary" type="submit">
              Cadastrar Parcelas
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalContainer>
  );
};

export default CadastroParcela;
